import React from "react";
import { Row, Col } from "reactstrap";

import * as moment from "moment";
import DOMPurify from "dompurify";
import {
  getOverlapStatusLabel,
  isConfirmedDuplicate,
} from "common/constants/overlap-status";

/**
 * The side-by-side comparison of an overlapping pair plus its resolution.
 *
 * Shared by the quick-view modal and the full details page so the two can't
 * drift apart; it renders bare rows and leaves the surrounding chrome (modal
 * body, card body) to the caller.
 */
const OverlappingPaymentDetails: React.FC<{ record: any }> = ({ record }) => {
  return (
    <>
      <Row className="mt-3 mb-4">
        <Col sm={6}>
          <h6 className="text-muted text-uppercase fw-semibold mb-3">
            First Payment Details
          </h6>

          <div className="table-responsive border border-dashed rounded p-2  mb-3">
            <table className="table table-md table-borderless table-striped mb-0">
              <tbody>
                <tr>
                  <td
                    className="fw-medium text-muted"
                    style={{ whiteSpace: "nowrap", width: "25%" }}
                  >
                    MDA & Region
                  </td>
                  <td>
                    <p className="fw-medium mb-0 fs-14">
                      {`${record?.payment?.mda?.name} - ${record?.payment?.mda?.region?.name}` ||
                        "Not Provided"}
                    </p>
                  </td>
                </tr>
                <tr>
                  <td
                    className="fw-medium text-muted"
                    style={{ whiteSpace: "nowrap", width: "25%" }}
                  >
                    Agency:
                  </td>
                  <td>{record?.payment?.agency?.name || "Not Provided"}</td>
                </tr>
                <tr>
                  <td className="fw-medium text-muted">Individual Name:</td>
                  <td>
                    {record?.payment?.individual?.fullName || "Not Provided"}
                  </td>
                </tr>

                <tr>
                  <td
                    className="fw-medium text-muted"
                    style={{ whiteSpace: "nowrap", width: "25%" }}
                  >
                    Position Title:
                  </td>
                  <td>{record?.payment?.positionTitle || "Not Provided"}</td>
                </tr>

                <tr>
                  <td className="fw-medium text-muted">Start Date:</td>
                  <td>
                    {record?.payment?.startDate &&
                      moment(new Date(record?.payment?.startDate)).format(
                        "DD MMM Y"
                      )}
                  </td>
                </tr>

                <tr>
                  <td className="fw-medium text-muted">End Date:</td>
                  <td>
                    {record?.payment?.endDate &&
                      moment(new Date(record?.payment?.endDate)).format(
                        "DD MMM Y"
                      )}
                  </td>
                </tr>

                <tr>
                  <td
                    className="fw-medium text-muted"
                    style={{ whiteSpace: "nowrap", width: "25%" }}
                  >
                    Amount / Month:
                  </td>
                  <td>
                    {(record?.payment?.amountPerMonth &&
                      "$" +
                        record?.payment?.amountPerMonth?.toLocaleString()) ||
                      "Not Provided"}
                  </td>
                </tr>

                <tr>
                  <td
                    className="fw-medium text-muted"
                    style={{ whiteSpace: "nowrap", width: "25%" }}
                  >
                    Created At:
                  </td>
                  <td>
                    {moment(new Date(record?.payment?.createdAt)).format(
                      "DD MMM Y"
                    ) || "Not Provided"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Col>
        <Col sm={6}>
          <h6 className="text-muted text-uppercase fw-semibold mb-3">
            Overlapping Payment Details
          </h6>

          <div className="table-responsive border border-dashed rounded p-2  mb-3 ">
            <table className="table table-md table-borderless table-striped mb-0">
              <tbody>
                <tr>
                  <td
                    className="fw-medium text-muted"
                    style={{ whiteSpace: "nowrap", width: "30%" }}
                  >
                    {" "}
                    MDA & Region
                  </td>
                  <td>
                    {`${record?.overlappingPayment?.mda?.name} - ${record?.overlappingPayment?.mda?.region?.name}` ||
                      "Not Provided"}
                  </td>
                </tr>

                <tr>
                  <td className="fw-medium text-muted">Agency:</td>
                  <td>
                    {record?.overlappingPayment?.agency?.name || "Not Provided"}
                  </td>
                </tr>
                <tr>
                  <td className="fw-medium text-muted">Individual Name:</td>
                  <td>
                    {record?.overlappingPayment?.individual?.fullName ||
                      "Not Provided"}
                  </td>
                </tr>
                <tr>
                  <td className="fw-medium text-muted">Position Title:</td>
                  <td>
                    {record?.overlappingPayment?.positionTitle || "Not Provided"}
                  </td>
                </tr>
                <tr>
                  <td className="fw-medium text-muted">Start Date:</td>
                  <td>
                    {record?.overlappingPayment?.startDate &&
                      moment(
                        new Date(record?.overlappingPayment?.startDate)
                      ).format("DD MMM Y")}
                  </td>
                </tr>
                <tr>
                  <td className="fw-medium text-muted">End Date:</td>
                  <td>
                    {record?.overlappingPayment?.endDate &&
                      moment(
                        new Date(record?.overlappingPayment?.endDate)
                      ).format("DD MMM Y")}
                  </td>
                </tr>
                <tr>
                  <td className="fw-medium text-muted">Amount / Month:</td>
                  <td>
                    {(record?.overlappingPayment?.amountPerMonth &&
                      "$" +
                        record?.overlappingPayment?.amountPerMonth?.toLocaleString()) ||
                      "Not Provided"}
                  </td>
                </tr>
                <tr>
                  <td className="fw-medium text-muted">Created At:</td>
                  <td>
                    {moment(
                      new Date(record?.overlappingPayment?.createdAt)
                    ).format("DD MMM Y") || "Not Provided"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={12} className="mb-4">
          <h6 className="text-muted text-uppercase fw-semibold mb-3">
            Additional Information
          </h6>

          <div className="table-responsive border border-dashed rounded p-2  mb-3">
            <table className="table table-md table-borderless table-striped mb-0">
              <tbody>
                <tr>
                  <td
                    className="fw-medium text-muted"
                    style={{ whiteSpace: "nowrap", width: "25%" }}
                  >
                    What is this payment?
                  </td>
                  <td>
                    <p className="fw-medium mb-0 fs-14">
                      {record?.overlapStatus ? (
                        <span
                          className={`badge fs-12 ${
                            isConfirmedDuplicate(record.overlapStatus)
                              ? "bg-danger-subtle text-danger"
                              : "bg-success-subtle text-success"
                          }`}
                        >
                          {getOverlapStatusLabel(record.overlapStatus)}
                        </span>
                      ) : (
                        <span className="badge bg-secondary-subtle text-secondary fs-12">
                          Not Set
                        </span>
                      )}
                    </p>
                  </td>
                </tr>
                <tr>
                  <td
                    className="fw-medium text-muted"
                    style={{ whiteSpace: "nowrap", width: "25%" }}
                  >
                    Has this overlapping payment been resolved?
                  </td>
                  <td>
                    {record?.resolved ? (
                      <span className="badge bg-success-subtle text-success fs-12">
                        Yes
                      </span>
                    ) : (
                      <span className="badge bg-danger-subtle text-danger fs-12">
                        No
                      </span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="fw-medium text-muted">
                    Comments on resolution:
                  </td>
                  <td
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        record?.comments || "Not Provided"
                      ),
                    }}
                  ></td>
                </tr>
              </tbody>
            </table>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default OverlappingPaymentDetails;
